import Guid from "@/app/types/common/guid";
import { GuidSet } from "@/app/types/common/guidcollections";
import { isNullish } from "@/app/apiutils";
import type { Maybe } from "@/components/utils/typeHelpers";
import { PiecemealByGuidsCache } from "@/app/caching/piecemealByGuidsCache";

/**
 * Cache Utility For Loading items by ids, but only as required.
 */
export class PiecemealQByGuidsCache<T> {
  private _baseCache: PiecemealByGuidsCache<T | Guid>;

  public constructor(
    extractId: (item: T) => Guid,
    loadItems: (ids: readonly Guid[]) => Promise<readonly T[]>
  ) {
    this._baseCache = new PiecemealByGuidsCache<T | Guid>(
      (item: T | Guid) => (item instanceof Guid ? item : extractId(item)),
      async (ids: readonly Guid[]) => {
        const read = await loadItems(ids);
        const readIds = new GuidSet(read.map((item) => extractId(item)));
        const missingIds = ids.filter((x) => !readIds.has(x));
        return [...read, ...missingIds];
      }
    );
  }

  async ensureLoaded(ids: readonly Guid[]) {
    await this._baseCache.ensureLoaded(ids);
  }

  get(id: Maybe<Guid>) {
    const val = this._baseCache.get(id);
    return !isNullish(val) && !(val instanceof Guid) ? val : null;
  }

  values() {
    return this._baseCache.values().filter((x) => !(x instanceof Guid));
  }
}
